import { createContext, useContext, useEffect, useState } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase } from './lib/supabase';

type AuthContextType = {
  user: User | null;
  loading: boolean;
};

export const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
});

export const useAuth = () => useContext(AuthContext);

export default function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      const timeout = setTimeout(() => {
        setLoading(false);
      }, 800);

      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user) {
          setUser(session.user);
        }
      } catch (err) {
        console.error('Auth init error:', err);
      } finally {
        clearTimeout(timeout);
        setLoading(false);
      }
    };

    initAuth();

    // Garde une référence stable au même user pour éviter de re-déclencher les
    // effets des consumers à chaque token refresh / changement de visibilité
    // d'onglet (Supabase recrée un objet user à chaque rafraîchissement).
    const setUserStable = (newUser: User | null) => {
      setUser((prev) => {
        if (prev?.id === newUser?.id) return prev; // même user → on garde la réf
        return newUser;
      });
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (event === 'SIGNED_IN' && session?.user) {
          setUserStable(session.user);

          // Restore pre-OAuth navigation target (set by auth-modal before LinkedIn redirect)
          try {
            const returnUrl = localStorage.getItem('auth_return_url');
            if (returnUrl && returnUrl !== window.location.pathname) {
              localStorage.removeItem('auth_return_url');
              // Use replace to avoid a back-button loop to the OAuth landing
              window.location.replace(returnUrl);
            }
          } catch {
            // localStorage unavailable — no redirect needed
          }
        } else if (event === 'SIGNED_OUT' || event === 'USER_DELETED') {
          setUserStable(null);
        } else if (event === 'TOKEN_REFRESHED' && session?.user) {
          setUserStable(session.user);
        }
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading }}>
      {children}
    </AuthContext.Provider>
  );
}